import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaCode, FaHeart, FaRocket } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const About = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getAbout();
        console.log('About response:', response.data);
        
        if (response.data) {
          // Ensure core_values is an array
          let coreValues = response.data.core_values || [];
          if (typeof coreValues === 'string') {
            try {
              coreValues = JSON.parse(coreValues) || [];
            } catch {
              coreValues = [];
            }
          }
          if (!Array.isArray(coreValues)) {
            coreValues = [];
          }
          
          // Ensure timeline is an array
          let timeline = response.data.timeline || [];
          if (typeof timeline === 'string') {
            try {
              timeline = JSON.parse(timeline) || [];
            } catch {
              timeline = [];
            }
          }
          if (!Array.isArray(timeline)) {
            timeline = [];
          }
          
          setAbout({
            ...response.data,
            core_values: coreValues,
            timeline: timeline
          });
        } else {
          // Fallback data if response is empty
          setAbout({
            title: 'Iliyasu Abdurrazaq',
            subtitle: 'Full Stack Software Engineer | Blockchain & Embedded Systems',
            mission: 'To build software that solves real-world problems and improves lives through technology.',
            vision: 'To become a leading software engineer known for innovation, excellence, and impact.',
            story: 'I am a passionate Full Stack Software Engineer with over 3 years of hands-on experience designing, building, and deploying modern software solutions across web, backend, blockchain, and embedded systems.',
            philosophy: 'I believe software should solve real-world problems, remain maintainable over time, and provide meaningful value to users rather than simply demonstrating technical complexity.',
            core_values: ['Innovation', 'Excellence', 'Impact', 'Integrity', 'Growth'],
            timeline: [
              { year: '2023 - Present', title: 'B.Sc. Software Engineering', description: 'Federal University Dutse, Nigeria' },
              { year: '2021 - Present', title: 'Freelance Full Stack Developer', description: 'Self-Employed - Remote' }
            ]
          });
        }
      } catch (error) {
        console.error('Error fetching about data:', error);
        setError('Failed to load about information');
        // Set fallback data on error
        setAbout({
          title: 'Iliyasu Abdurrazaq',
          subtitle: 'Full Stack Software Engineer | Blockchain & Embedded Systems',
          mission: 'To build software that solves real-world problems and improves lives through technology.',
          vision: 'To become a leading software engineer known for innovation, excellence, and impact.',
          story: 'I am a passionate Full Stack Software Engineer with over 3 years of hands-on experience.',
          philosophy: 'I believe software should solve real-world problems and provide meaningful value to users.',
          core_values: ['Innovation', 'Excellence', 'Impact', 'Integrity', 'Growth'],
          timeline: [
            { year: '2023 - Present', title: 'B.Sc. Software Engineering', description: 'Federal University Dutse, Nigeria' }
          ]
        });
      } finally {
        setLoading(false);
      }
    };
    fetchAbout();
  }, []);

  const stats = [
    { label: 'Years Experience', value: '3+' },
    { label: 'Projects Completed', value: '15+' },
    { label: 'Happy Clients', value: '10+' },
    { label: 'Technologies', value: '20+' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            About Me
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            Know Who <span className="gradient-text">I Am</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            A passionate software engineer dedicated to building innovative solutions
          </p>
        </motion.div>

        {/* My Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-3xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-4">My Story</h2>
          <p className="text-gray-300 leading-relaxed">
            {about?.story || 'I am a passionate Full Stack Software Engineer with over 3 years of hands-on experience designing, building, and deploying modern software solutions.'}
          </p>
        </motion.div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-3xl p-8"
          >
            <h3 className="text-xl font-bold text-white mb-3">Mission</h3>
            <p className="text-gray-300 leading-relaxed">
              {about?.mission || 'To build software that solves real-world problems and improves lives through technology.'}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-3xl p-8"
          >
            <h3 className="text-xl font-bold text-white mb-3">Vision</h3>
            <p className="text-gray-300 leading-relaxed">
              {about?.vision || 'To become a leading software engineer known for innovation, excellence, and impact.'}
            </p>
          </motion.div>
        </div>

        {/* Philosophy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="glass rounded-3xl p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-4">My Development Philosophy</h2>
          <blockquote className="text-gray-300 text-lg italic border-l-4 border-primary-500 pl-4">
            "{about?.philosophy || 'I believe software should solve real-world problems, remain maintainable over time, and provide meaningful value to users.'}"
          </blockquote>
        </motion.div>

        {/* Core Values */}
        {about?.core_values && about.core_values.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {about.core_values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="glass rounded-xl p-6 text-center hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-primary-400">✦</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg">{value}</h3>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Timeline */}
        {about?.timeline && about.timeline.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Journey Timeline</h2>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-primary-500/30" />
              
              {about.timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center mb-8 ${
                    index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                  }`}
                >
                  <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="glass rounded-xl p-4 hover:bg-white/10 transition-all duration-300">
                      <div className="text-primary-400 font-medium">{item.year}</div>
                      <h3 className="text-white font-semibold mt-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm mt-1">{item.description}</p>
                    </div>
                  </div>

                  <div className="absolute left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-primary-500/20 border-2 border-primary-500 flex items-center justify-center text-primary-400 z-10">
                    <span className="text-sm">●</span>
                  </div>

                  <div className="w-5/12" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {stats.map((stat, index) => (
            <div key={index} className="glass rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default About;